import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Loader2,
  Edit,
  X,
  Save,
  Archive,
  ArchiveRestore,
  ShieldCheck,
  Activity,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  usePatient,
  useUpdatePatient,
  useArchivePatient,
  useDecryptCpr,
} from '@/hooks/usePatients';
import { ApiCallError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { JournalSection } from '@/components/journal/JournalSection';
import { PatientDocumentsSection } from '@/components/documents/PatientDocumentsSection';
import { SygeforsikringTab } from '@/components/sygeforsikring/SygeforsikringTab';
import { InvoiceTab } from '@/components/patient/InvoiceTab';
import { AppointmentTab } from '@/components/patient/AppointmentTab';
import { SmsTab } from '@/components/patient/SmsTab';
import { MessageTab } from '@/components/patient/MessageTab';
import { EmailHistoryTab } from '@/components/patient/EmailHistoryTab';
import { PunchCardTab } from '@/components/patient/PunchCardTab';
import { TaskTab } from '@/components/patient/TaskTab';
import { FormsAssignmentTab } from '@/components/patient/FormsAssignmentTab';
import { ConsentTab } from '@/components/patient/ConsentTab';
import { PatientReferralSagsakterTab } from '@/components/patient/PatientReferralSagsakterTab';
import { PatientOverviewPanel } from '@/components/patient/PatientOverviewPanel';
import { useClinicSdEnabled } from '@/hooks/useSygeforsikring';
import { useClinicType } from '@/hooks/useClinicType';
import { useClinic } from '@/hooks/useClinicSettings';
import { useReferralIdByPatient } from '@/hooks/useReferrals';
import {
  isClinicModuleEnabled,
  readActivatedModules,
} from '@/lib/clinicModules';
import type { Json } from '@/types/database.generated';

interface AddressShape {
  street: string;
  zip_code: string;
  city: string;
  country: string;
}

const EMPTY_ADDRESS: AddressShape = {
  street: '',
  zip_code: '',
  city: '',
  country: 'Danmark',
};

function readAddress(value: Json | null): AddressShape {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return EMPTY_ADDRESS;
  }
  const v = value as Record<string, Json>;
  return {
    street: typeof v.street === 'string' ? v.street : '',
    // We accept both `zip` and `zip_code` for v1 compat.
    zip_code: typeof v.zip_code === 'string' ? v.zip_code : typeof v.zip === 'string' ? v.zip : '',
    city: typeof v.city === 'string' ? v.city : '',
    country: typeof v.country === 'string' ? v.country : 'Danmark',
  };
}

export function PatientDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isClinicAdmin, isPlatformAdmin, user, hasPermission } = useAuth();
  const { activeClinicId } = useActiveClinic();
  const { data: patient, isLoading, error } = usePatient(id);
  const update = useUpdatePatient();
  const archive = useArchivePatient();
  const decrypt = useDecryptCpr();
  const { data: sdInfo } = useClinicSdEnabled(activeClinicId);
  const { isDentist } = useClinicType();
  const { data: clinic } = useClinic(activeClinicId);
  const referralsModuleEnabled = isClinicModuleEnabled(
    readActivatedModules(clinic?.activated_modules),
    'referrals',
  );
  const { data: referralId } = useReferralIdByPatient(
    referralsModuleEnabled ? id : undefined,
  );

  const [editing, setEditing] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState<AddressShape>(EMPTY_ADDRESS);
  const [notes, setNotes] = useState('');
  const [marketingEmail, setMarketingEmail] = useState(false);
  const [marketingSms, setMarketingSms] = useState(false);
  const [preferredLocale, setPreferredLocale] = useState<'da-DK' | 'en-DK'>('da-DK');
  const [cprChange, setCprChange] = useState<string | null>(null);

  const [revealedCpr, setRevealedCpr] = useState<string | null>(null);

  type TabType =
    | 'appointments'
    | 'journal'
    | 'documents'
    | 'invoices'
    | 'punch_cards'
    | 'sygeforsikring'
    | 'sms'
    | 'messages'
    | 'email'
    | 'consent'
    | 'tasks'
    | 'questionnaires'
    | 'dental'
    | 'sagsakter';
  const [activeTab, setActiveTab] = useState<TabType>('appointments');

  useEffect(() => {
    if (!patient) return;
    setFirstName(patient.first_name);
    setLastName(patient.last_name);
    setEmail(patient.email ?? '');
    setPhone(patient.phone ?? '');
    setAddress(readAddress(patient.address));
    setNotes(patient.notes ?? '');
    setMarketingEmail(patient.marketing_consent_email);
    setMarketingSms(patient.marketing_consent_sms);
    setPreferredLocale((patient.preferred_locale as 'da-DK' | 'en-DK') ?? 'da-DK');
    setCprChange(null);
    setRevealedCpr(null);
  }, [patient]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-page-light dark:bg-page-dark">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="min-h-screen bg-page-light p-6 dark:bg-page-dark">
        <ErrorAlert message={t('patients.error_not_found')} />
        <Button variant="secondary" onClick={() => navigate('/patients')}>
          {t('patients.back_to_list')}
        </Button>
      </div>
    );
  }

  const canEdit = Boolean(
    activeClinicId &&
    activeClinicId === patient.clinic_id &&
    (isPlatformAdmin || isClinicAdmin(patient.clinic_id) || true), // any clinic member can edit
  );
  const canArchive = canEdit;
  const canRevealCpr = Boolean(
    activeClinicId === patient.clinic_id &&
      hasPermission(patient.clinic_id, 'patients_cpr', 'view'),
  );
  const canEditCpr = Boolean(
    activeClinicId === patient.clinic_id &&
      hasPermission(patient.clinic_id, 'patients_cpr', 'update'),
  );

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    const payload: Parameters<typeof update.mutateAsync>[0] = {
      patient_id: patient.id,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim() || null,
      phone: phone.trim() || null,
      address: {
        street: address.street.trim() || undefined,
        city: address.city.trim() || undefined,
        zip_code: address.zip_code.trim() || undefined,
        country: address.country.trim() || undefined,
      },
      notes: notes.trim() || null,
      marketing_consent_email: marketingEmail,
      marketing_consent_sms: marketingSms,
      preferred_locale: preferredLocale,
    };

    if (canEditCpr && cprChange !== null) {
      const trimmed = cprChange.trim();
      if (trimmed.length > 0) {
        const digits = trimmed.replace(/\D/g, '');
        if (digits.length !== 10) {
          setSubmitError(t('patients.errors.cpr_format'));
          return;
        }
        payload.cpr = trimmed;
      } else {
        payload.cpr = null;
      }
    }

    try {
      await update.mutateAsync(payload);
      toast.success(t('patients.toast_updated'));
      setEditing(false);
      setCprChange(null);
      setRevealedCpr(null);
    } catch (err) {
      if (err instanceof ApiCallError) {
        setSubmitError(err.message);
      } else {
        setSubmitError(t('patients.errors.unknown'));
      }
    }
  };

  const onToggleArchive = async () => {
    const willArchive = patient.status === 'active';
    if (
      !window.confirm(willArchive ? t('patients.confirm_archive') : t('patients.confirm_restore'))
    ) {
      return;
    }
    try {
      await archive.mutateAsync({ patient_id: patient.id, archive: willArchive });
      toast.success(willArchive ? t('patients.toast_archived') : t('patients.toast_restored'));
    } catch (err) {
      if (err instanceof ApiCallError) {
        toast.error(err.message);
      } else {
        toast.error(t('patients.errors.unknown'));
      }
    }
  };

  const onRevealCpr = async () => {
    if (revealedCpr) {
      setRevealedCpr(null);
      return;
    }
    try {
      const r = await decrypt.mutateAsync({
        patient_id: patient.id,
        source: 'patient-detail',
        purpose: 'manual reveal from patient detail page',
      });
      setRevealedCpr(r.cpr);
      toast.success(t('patients.toast_cpr_revealed'));
    } catch (err) {
      if (err instanceof ApiCallError) {
        toast.error(err.message);
      } else {
        toast.error(t('patients.errors.unknown'));
      }
    }
  };

  return (
    <div className="min-h-screen bg-page-light dark:bg-page-dark">
      <main className="py-6">
        <div className="max-w-none px-6">
          <button
            type="button"
            onClick={() => navigate('/patients')}
            className="mb-4 inline-flex items-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
          >
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('patients.back_to_list')}
          </button>

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-2xl font-bold leading-7 text-gray-900 dark:text-white sm:text-3xl">
                  {patient.first_name} {patient.last_name}
                </h2>
                {patient.status === 'archived' && (
                  <span className="inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
                    {t('patients.status_archived')}
                  </span>
                )}
              </div>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {patient.pat_number ? `#${patient.pat_number}` : t('patients.no_pat_number')}
                {' · '}
                {t('patients.created_at_label')} {new Date(patient.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {!editing && canEdit && (
                <Button onClick={() => setEditing(true)}>
                  <Edit className="mr-2 h-4 w-4" />
                  {t('common.edit')}
                </Button>
              )}
              {editing && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setEditing(false);
                    setSubmitError(null);
                    setCprChange(null);
                  }}
                  disabled={update.isPending}
                >
                  <X className="mr-2 h-4 w-4" />
                  {t('common.cancel')}
                </Button>
              )}
            </div>
          </div>

          {submitError && (
            <div className="mt-6">
              <ErrorAlert message={submitError} />
            </div>
          )}

          {editing ? (
            <form
              onSubmit={onSubmit}
              className="mt-6 space-y-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-card-dark"
            >
              <Section title={t('patients.section_basic')}>
                <Grid>
                  <Input
                    label={t('patients.field_first_name')}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                  <Input
                    label={t('patients.field_last_name')}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                  <Input
                    label={t('patients.field_email')}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <Input
                    label={t('patients.field_phone')}
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </Grid>
              </Section>

              {canEditCpr && (
                <Section
                  title={t('patients.section_cpr')}
                  hint={
                    <span className="inline-flex items-center text-emerald-700 dark:text-emerald-300">
                      <ShieldCheck className="mr-1 h-3.5 w-3.5" />
                      {t('patients.cpr_encryption_note')}
                    </span>
                  }
                >
                  {cprChange === null ? (
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-gray-700 dark:text-gray-200">
                        {patient.has_cpr
                          ? t('patients.cpr_currently_set')
                          : t('patients.cpr_currently_unset')}
                      </span>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setCprChange('')}
                      >
                        {patient.has_cpr ? t('patients.cpr_replace') : t('patients.cpr_set')}
                      </Button>
                      {patient.has_cpr && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setCprChange('__CLEAR__')}
                        >
                          {t('patients.cpr_clear')}
                        </Button>
                      )}
                    </div>
                  ) : cprChange === '__CLEAR__' ? (
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-amber-700 dark:text-amber-300">
                        {t('patients.cpr_will_clear_on_save')}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setCprChange(null)}
                      >
                        {t('common.undo')}
                      </Button>
                    </div>
                  ) : (
                    <Input
                      label={t('patients.field_cpr')}
                      value={cprChange}
                      onChange={(e) => setCprChange(e.target.value)}
                      placeholder="1234567890"
                      hint={t('patients.field_cpr_hint')}
                      autoFocus
                    />
                  )}
                </Section>
              )}

              <Section title={t('patients.section_address')}>
                <Grid>
                  <div className="sm:col-span-2">
                    <Input
                      label={t('patients.field_street')}
                      value={address.street}
                      onChange={(e) => setAddress({ ...address, street: e.target.value })}
                    />
                  </div>
                  <Input
                    label={t('patients.field_zip')}
                    value={address.zip_code}
                    onChange={(e) => setAddress({ ...address, zip_code: e.target.value })}
                  />
                  <Input
                    label={t('patients.field_city')}
                    value={address.city}
                    onChange={(e) => setAddress({ ...address, city: e.target.value })}
                  />
                  <div className="sm:col-span-2">
                    <Input
                      label={t('patients.field_country')}
                      value={address.country}
                      onChange={(e) => setAddress({ ...address, country: e.target.value })}
                    />
                  </div>
                </Grid>
              </Section>

              <Section title={t('patients.section_notes')}>
                <textarea
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
              </Section>

              <Section title={t('patients.section_consent')}>
                <div className="space-y-3">
                  <ConsentToggle
                    checked={marketingEmail}
                    onChange={setMarketingEmail}
                    label={t('patients.consent_marketing_email')}
                    description={t('patients.consent_marketing_email_desc')}
                  />
                  <ConsentToggle
                    checked={marketingSms}
                    onChange={setMarketingSms}
                    label={t('patients.consent_marketing_sms')}
                    description={t('patients.consent_marketing_sms_desc')}
                  />
                </div>
              </Section>

              <Section title={t('patients.section_language', 'Sprog')}>
                <div>
                  <label
                    htmlFor="patient-locale"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-200"
                  >
                    {t('patients.field_preferred_locale', 'Foretrukket sprog')}
                  </label>
                  <select
                    id="patient-locale"
                    value={preferredLocale}
                    onChange={(e) => setPreferredLocale(e.target.value as 'da-DK' | 'en-DK')}
                    className="mt-1 block h-[44px] w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:max-w-xs"
                  >
                    <option value="da-DK">Dansk (da-DK)</option>
                    <option value="en-DK">Engelsk (en-DK)</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    {t('patients.field_preferred_locale_hint', 'Bruges til spørgeskema-invitationer fra Clinio Forms')}
                  </p>
                </div>
              </Section>

              <div className="flex items-center justify-between gap-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                {canArchive && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={onToggleArchive}
                    disabled={archive.isPending || update.isPending}
                  >
                    {patient.status === 'active' ? (
                      <>
                        <Archive className="mr-2 h-4 w-4" />
                        {t('patients.archive')}
                      </>
                    ) : (
                      <>
                        <ArchiveRestore className="mr-2 h-4 w-4" />
                        {t('patients.restore')}
                      </>
                    )}
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={update.isPending || !firstName.trim() || !lastName.trim()}
                  className="ml-auto"
                >
                  {update.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      {t('common.save')}
                    </>
                  )}
                </Button>
              </div>
            </form>
          ) : (
            <PatientOverviewPanel
              patient={patient}
              canEdit={canEdit}
              canRevealCpr={canRevealCpr}
              revealedCpr={revealedCpr}
              onRevealCpr={onRevealCpr}
              cprLoading={decrypt.isPending}
            />
          )}

          {activeClinicId && (
            <div className="mt-4">
              {/* Tab Navigation */}
              <div className="border-b border-gray-200 dark:border-gray-700">
                <nav className="-mb-px flex space-x-4 overflow-x-auto scrollbar-hide">
                  <button
                    onClick={() => setActiveTab('appointments')}
                    className={`
                      whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                      ${
                        activeTab === 'appointments'
                          ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                      }
                    `}
                  >
                    {t('patients.tabs.appointments')}
                  </button>
                  <button
                    onClick={() => setActiveTab('journal')}
                    className={`
                      whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                      ${
                        activeTab === 'journal'
                          ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                      }
                    `}
                  >
                    {t('patients.tabs.journal')}
                  </button>
                  {isDentist && (
                    <button
                      onClick={() => navigate(`/patients/${patient.id}/tand`)}
                      className="flex items-center gap-1.5 whitespace-nowrap border-b-2 border-transparent px-1 py-4 text-sm font-medium text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300"
                    >
                      <Activity className="h-4 w-4" />
                      {t('dental.module.openButton')}
                    </button>
                  )}
                  <button
                    onClick={() => setActiveTab('documents')}
                    className={`
                      whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                      ${
                        activeTab === 'documents'
                          ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                      }
                    `}
                  >
                    {t('patients.tabs.documents')}
                  </button>
                  {referralsModuleEnabled && referralId && (
                    <button
                      onClick={() => setActiveTab('sagsakter')}
                      className={`
                        whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                        ${
                          activeTab === 'sagsakter'
                            ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                        }
                      `}
                    >
                      {t('patients.tabs.sagsakter')}
                    </button>
                  )}
                  <button
                    onClick={() => setActiveTab('invoices')}
                    className={`
                      whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                      ${
                        activeTab === 'invoices'
                          ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                      }
                    `}
                  >
                    {t('patients.tabs.invoices')}
                  </button>
                  <button
                    onClick={() => setActiveTab('punch_cards')}
                    className={`
                      whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                      ${
                        activeTab === 'punch_cards'
                          ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                      }
                    `}
                  >
                    {t('patients.tabs.punch_cards', 'Punch Cards')}
                  </button>
                  {sdInfo?.enabled && (
                    <button
                      onClick={() => setActiveTab('sygeforsikring')}
                      className={`
                        whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                        ${
                          activeTab === 'sygeforsikring'
                            ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                            : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                        }
                      `}
                    >
                      {t('patients.tabs.sygeforsikring')}
                    </button>
                  )}
                  <button
                    onClick={() => setActiveTab('sms')}
                    className={`
                      whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                      ${
                        activeTab === 'sms'
                          ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                      }
                    `}
                  >
                    {t('patients.tabs.sms', 'SMS')}
                  </button>
                  <button
                    onClick={() => setActiveTab('messages')}
                    className={`
                      whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                      ${
                        activeTab === 'messages'
                          ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                      }
                    `}
                  >
                    {t('patients.tabs.messages', 'Messages')}
                  </button>
                  <button
                    onClick={() => setActiveTab('email')}
                    className={`
                      whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                      ${
                        activeTab === 'email'
                          ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                      }
                    `}
                  >
                    {t('patients.tabs.email', 'Email')}
                  </button>
                  <button
                    onClick={() => setActiveTab('consent')}
                    className={`
                      whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                      ${
                        activeTab === 'consent'
                          ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                      }
                    `}
                  >
                    {t('patients.tabs.consent', 'Consent')}
                  </button>
                  <button
                    onClick={() => setActiveTab('tasks')}
                    className={`
                      whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                      ${
                        activeTab === 'tasks'
                          ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                      }
                    `}
                  >
                    {t('patients.tabs.tasks', 'Tasks')}
                  </button>
                  <button
                    onClick={() => setActiveTab('questionnaires')}
                    className={`
                      whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium
                      ${
                        activeTab === 'questionnaires'
                          ? 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-400'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-300'
                      }
                    `}
                  >
                    {t('patients.tabs.questionnaires', 'Questionnaires')}
                  </button>
                </nav>
              </div>

              {/* Tab Content */}
              <div className="mt-6">
                {activeTab === 'appointments' && (
                  <AppointmentTab patientId={patient.id} clinicId={activeClinicId} />
                )}
                {activeTab === 'journal' && (
                  <JournalSection
                    clinicId={activeClinicId}
                    patientId={patient.id}
                    currentUserId={user?.id}
                    isAdmin={isPlatformAdmin || isClinicAdmin(activeClinicId)}
                  />
                )}
                {/* dental view moved to /patients/:id/tand (DentalModulePage) */}
                {activeTab === 'documents' && (
                  <PatientDocumentsSection
                    clinicId={activeClinicId}
                    patientId={patient.id}
                    isAdmin={isPlatformAdmin || isClinicAdmin(activeClinicId)}
                  />
                )}
                {activeTab === 'sagsakter' &&
                  referralsModuleEnabled &&
                  referralId && (
                    <PatientReferralSagsakterTab patientId={patient.id} />
                  )}
                {activeTab === 'invoices' && (
                  <InvoiceTab clinicId={patient.clinic_id} patientId={patient.id} />
                )}
                {activeTab === 'punch_cards' && (
                  <PunchCardTab clinicId={activeClinicId} patientId={patient.id} />
                )}
                {activeTab === 'sygeforsikring' && sdInfo?.enabled && (
                  <SygeforsikringTab
                    clinicId={activeClinicId}
                    cpr={revealedCpr}
                    onRequestCprReveal={canRevealCpr ? onRevealCpr : undefined}
                  />
                )}
                {activeTab === 'sms' && (
                  <SmsTab clinicId={activeClinicId} patientId={patient.id} />
                )}
                {activeTab === 'messages' && (
                  <MessageTab clinicId={activeClinicId} patientId={patient.id} />
                )}
                {activeTab === 'email' && (
                  <EmailHistoryTab clinicId={patient.clinic_id} patientId={patient.id} />
                )}
                {activeTab === 'consent' && (
                  <ConsentTab clinicId={activeClinicId} patientId={patient.id} />
                )}
                {activeTab === 'tasks' && (
                  <TaskTab clinicId={activeClinicId} patientId={patient.id} />
                )}
                {activeTab === 'questionnaires' && (
                  <FormsAssignmentTab clinicId={activeClinicId} patientId={patient.id} />
                )}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h3>
        {hint && <span className="text-xs">{hint}</span>}
      </div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-4 sm:grid-cols-2">{children}</div>;
}

function ConsentToggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  description: string;
}) {
  return (
    <label className="flex items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700"
      />
      <span>
        <span className="block text-sm font-medium text-gray-900 dark:text-white">{label}</span>
        <span className="block text-xs text-gray-500 dark:text-gray-400">{description}</span>
      </span>
    </label>
  );
}
